import { useMutation } from "@tanstack/react-query";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { Button } from "@/components/ui/button";
import { INTERVIEW_CHAPTERS, INTERVIEW_START } from "@shared/interview/chapters";

/**
 * INTERVIEW-SHELL-1 (s205) — the MODE intro before chapter one (D-202.24).
 *
 * Deliberately the `FFExplainer mode="interstitial"` pattern: a full-screen surface keyed on a
 * server-persisted flag, text first, ONE button, and the refetch that flips the ladder's condition.
 * D-202.24 allows an optional video here and rules it text-first; `INTRO-VIDEOS-1` keeps the video
 * out of this build, so this is the text half only.
 *
 * 🔴 COPY IS PLACEHOLDER. Chapter and intro copy are gated on SPEC (D-202.22), so every planner-facing
 * sentence below is marked and must be replaced before this reaches a real planner. The STRUCTURE is
 * what the pilot is measuring — the two-numbers promise, one button, no second path.
 */
export function InterviewIntro({ clientId }: { clientId: string }) {
  const start = useMutation({
    mutationFn: async () => {
      await apiRequest("PATCH", `/api/clients/${clientId}/interview-position`, {
        position: INTERVIEW_START,
      });
    },
    onSuccess: () => {
      // Refetching the client flips useInterviewGate from "intro" to "shell".
      queryClient.invalidateQueries({ queryKey: ["/api/clients"] });
    },
  });

  return (
    <div className="min-h-screen w-full overflow-y-auto bg-background flex justify-center px-4 py-10">
      <div className="w-full max-w-[640px] rounded-2xl border border-warm-stone bg-white shadow-sm px-6 py-8 sm:px-10 sm:py-10">
        <h1 className="font-serif text-[28px] sm:text-[32px] leading-tight text-foreground mb-6">
          Let's build your plan together
        </h1>

        <p className="text-[15px] text-foreground/90 leading-relaxed">
          [SPEC copy pending] We'll walk through {INTERVIEW_CHAPTERS.length} short chapters. Most of it
          you can answer from memory — there's nothing you need to go and find first.
        </p>

        {/* D-202.24's two-numbers promise, in placeholder words. The real numbers are live on every
            screen of the shell; this is the promise that they will be. */}
        <p className="mt-4 text-[15px] text-foreground/90 leading-relaxed">
          [SPEC copy pending] As you answer, you'll see two numbers move at the top of the screen: how
          many jobs the person settling your estate would face, and how many of those are ready to go.
          Answering a question can remove jobs entirely. Filling in a fact makes one ready.
        </p>

        <p className="mt-4 text-[15px] text-foreground/90 leading-relaxed">
          [SPEC copy pending] Nothing is saved as a draft — every answer is kept the moment you give it,
          so you can stop whenever you like and pick up where you left off.
        </p>

        <div className="mt-9">
          <Button
            className="w-full"
            size="lg"
            onClick={() => start.mutate()}
            disabled={start.isPending}
            data-testid="interview-intro-start"
          >
            {start.isPending ? "One moment…" : "Let's start"}
          </Button>
          {start.isError && (
            <p className="mt-3 text-[13px] text-destructive" data-testid="interview-intro-error">
              That didn't save. Please try again.
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
